import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, Package, CheckCircle, User, Star } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { ProductImage } from "@/components/ProductImage";
import { EmptyState } from "@/components/States";
import { formatPrice, toNeDigits } from "@/lib/constants";
import { toast } from "sonner";
import { LEADS } from "@/constants/testIds";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SellerDashboard = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [leads, setLeads] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/my/products").then((res) => setProducts(res.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get("/my/leads").then((res) => setLeads(res.data)).catch(() => {});
  }, []);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    await api.delete(`/products/${id}`);
    toast.success(t("toast.productDeleted"));
    load();
  };

  const active = products.filter((p) => p.status === "active");

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-[#14251F]">{t("dashboard.title")}</h1>
        <button data-testid="dashboard-add-btn" onClick={() => navigate("/dashboard/add")} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#123A2C] text-white font-medium hover:bg-[#0D2C21] transition-colors">
          <Plus className="w-5 h-5" /> {t("dashboard.addNew")}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-6 mt-5">
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#DCE7E2] flex items-center justify-center"><Package className="w-5 h-5 text-[#123A2C]" /></div>
          <div>
            <div className="text-2xl font-extrabold text-[#123A2C]">{toNeDigits(products.length, lang)}</div>
            <div className="text-xs text-[#6B7280]">{t("dashboard.totalListings")}</div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#DCE7E2] flex items-center justify-center"><CheckCircle className="w-5 h-5 text-[#123A2C]" /></div>
          <div>
            <div className="text-2xl font-extrabold text-[#123A2C]">{toNeDigits(active.length, lang)}</div>
            <div className="text-xs text-[#6B7280]">{t("dashboard.activeListings")}</div>
          </div>
        </div>
      </div>

      <h2 className="text-lg font-bold text-[#14251F] mt-8 mb-4">{t("dashboard.myListings")}</h2>
      {loading ? (
        <div className="text-center py-10 text-[#6B7280]">{t("empty.loading")}</div>
      ) : products.length === 0 ? (
        <EmptyState message={t("empty.noListings")} testId="dashboard-empty" />
      ) : (
        <div className="space-y-3">
          {products.map((p) => (
            <div key={p.id} data-testid={`dashboard-listing-${p.id}`} className="bg-white rounded-xl border border-[#E5E7EB] p-3 flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#DCE7E2] shrink-0">
                <ProductImage images={p.images} seed={p.id} alt={p.title} className="w-full h-full" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#14251F] truncate">{p.title}</div>
                <div className="text-sm font-bold text-[#123A2C]">{t("common.rs")} {formatPrice(p.price, lang)}</div>
                <div className="text-xs text-[#6B7280]">{p.district} · <span className={p.status === "active" ? "text-[#16A34A]" : "text-[#DC2626]"}>{p.status === "active" ? t("dashboard.active") : t("dashboard.removed")}</span></div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`view-${p.id}`} onClick={() => navigate(`/product/${p.id}`)} className="p-2 rounded-lg text-[#6B7280] hover:bg-[#F6F7F4]"><Eye className="w-4.5 h-4.5" /></button>
                <button data-testid={`edit-${p.id}`} onClick={() => navigate(`/dashboard/edit/${p.id}`)} className="p-2 rounded-lg text-[#123A2C] hover:bg-[#F6F7F4]"><Pencil className="w-4.5 h-4.5" /></button>
                <button data-testid={`delete-${p.id}`} onClick={() => setDeleteId(p.id)} className="p-2 rounded-lg text-[#DC2626] hover:bg-red-50"><Trash2 className="w-4.5 h-4.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="text-lg font-bold text-[#14251F] mt-8 mb-4">{t("dashboard.leads")}</h2>
      {leads.length === 0 ? (
        <p className="text-sm text-[#6B7280]">{t("dashboard.noLeads")}</p>
      ) : (
        <div className="space-y-3" data-testid={LEADS.list}>
          {leads.map((l) => (
            <div key={l.id} data-testid={`${LEADS.item}-${l.id}`} className="bg-white rounded-xl border border-[#E5E7EB] p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#DCE7E2] flex items-center justify-center shrink-0">
                <User className="w-5 h-5 text-[#123A2C]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#14251F] truncate">{l.buyer_name}</div>
                <div className="text-xs text-[#6B7280] truncate">
                  {l.product_title} · {toNeDigits(new Date(l.created_at).toLocaleDateString(lang === "ne" ? "ne-NP" : "en-GB"), lang)}
                </div>
              </div>
              <Link
                to={`/user/${l.buyer_id}`}
                data-testid={`${LEADS.rateLink}-${l.id}`}
                className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#F5A623] text-[#412402] text-sm font-semibold hover:brightness-95 transition"
              >
                <Star className="w-4 h-4" /> {t("dashboard.rateBuyer")}
              </Link>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dashboard.delete")}?</AlertDialogTitle>
            <AlertDialogDescription>{t("dashboard.deleteConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">{t("form.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={doDelete} className="bg-[#DC2626] hover:bg-red-700">{t("dashboard.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SellerDashboard;
